import { useState } from 'react';
import { Button } from '../ui/button';
import { X, Plus, AlignCenter, AlignLeft, AlignRight, Bold, Italic } from 'lucide-react';

const FONTS = ['Arial', 'Georgia', 'Impact', 'Courier New', 'Comic Sans MS', 'Trebuchet MS'];
const COLORS = ['#ffffff', '#000000', '#ff0000', '#ff6600', '#ffcc00', '#00ff00', '#00ccff', '#0066ff', '#9933ff', '#ff33cc'];

export default function TextPanel({ onAdd, onClose, selectedElement, onUpdate, onDelete }) {
  const [text, setText] = useState(selectedElement?.text || '');
  const [fontSize, setFontSize] = useState(selectedElement?.fontSize || 24);
  const [fontFamily, setFontFamily] = useState(selectedElement?.fontFamily || 'Arial');
  const [fill, setFill] = useState(selectedElement?.fill || '#ffffff');
  const [fontStyle, setFontStyle] = useState(selectedElement?.fontStyle || 'normal');
  const [align, setAlign] = useState(selectedElement?.align || 'center');
  const [bgColor, setBgColor] = useState(selectedElement?.bgColor || null);

  const apply = () => {
    const config = { text: text || 'Text', fontSize, fontFamily, fill, fontStyle, align, bgColor };
    if (selectedElement) {
      onUpdate(config);
    } else {
      onAdd(config);
    }
    setText('');
    onClose();
  };

  return (
    <div className="bg-card border-t border-border p-3 space-y-3 max-h-64 overflow-y-auto" data-testid="text-panel">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{selectedElement ? 'Edit Text' : 'Add Text'}</span>
        <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>

      <input value={text} onChange={e => setText(e.target.value)} placeholder="Type your text..."
        className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
        autoFocus data-testid="text-input" />

      {/* Font */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {FONTS.map(f => (
          <button key={f} onClick={() => setFontFamily(f)}
            className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap border transition-all ${fontFamily === f ? 'border-violet-500 bg-violet-500/10' : 'border-border'}`}
            style={{ fontFamily: f }}>{f.split(' ')[0]}</button>
        ))}
      </div>

      {/* Size + style */}
      <div className="flex items-center gap-2">
        <input type="range" min="12" max="72" value={fontSize} onChange={e => setFontSize(parseInt(e.target.value))}
          className="flex-1 accent-violet-500" />
        <span className="text-xs text-muted-foreground w-8">{fontSize}</span>
        <button onClick={() => setFontStyle(fontStyle === 'bold' ? 'normal' : 'bold')}
          className={`w-8 h-8 rounded flex items-center justify-center ${fontStyle === 'bold' ? 'bg-violet-500/20 text-violet-400' : 'text-muted-foreground/70'}`}>
          <Bold className="w-4 h-4" />
        </button>
        <button onClick={() => setFontStyle(fontStyle === 'italic' ? 'normal' : 'italic')}
          className={`w-8 h-8 rounded flex items-center justify-center ${fontStyle === 'italic' ? 'bg-violet-500/20 text-violet-400' : 'text-muted-foreground/70'}`}>
          <Italic className="w-4 h-4" />
        </button>
      </div>

      {/* Colors */}
      <div className="flex gap-1.5">
        {COLORS.map(c => (
          <button key={c} onClick={() => setFill(c)}
            className={`w-7 h-7 rounded-full border-2 transition-all ${fill === c ? 'border-violet-400 scale-110' : 'border-transparent'}`}
            style={{ backgroundColor: c }} />
        ))}
      </div>

      {/* Align */}
      <div className="flex items-center gap-1">
        {[{ v: 'left', I: AlignLeft }, { v: 'center', I: AlignCenter }, { v: 'right', I: AlignRight }].map(a => (
          <button key={a.v} onClick={() => setAlign(a.v)}
            className={`w-8 h-8 rounded flex items-center justify-center ${align === a.v ? 'bg-violet-500/20 text-violet-400' : 'text-muted-foreground/70'}`}>
            <a.I className="w-4 h-4" />
          </button>
        ))}
      </div>

      <Button onClick={apply} className="w-full bg-violet-600 hover:bg-violet-700" size="sm" data-testid="text-apply">
        <Plus className="w-4 h-4 mr-1" /> {selectedElement ? 'Update' : 'Add Text'}
      </Button>
    </div>
  );
}
